import React, { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as LinkedinIcon } from '../../assets/linkedin.svg';
import { ReactComponent as GithubIcon } from '../../assets/github-nav.svg';
import { ReactComponent as WhatsappIcon } from '../../assets/whatsapp.svg';
import { APP_CONFIG } from '../../constants/config';
import { scrollToElement } from '../../utils/helpers';

const Footer = () => {
  const { t } = useTranslation();

  const handleNavClick = useCallback((e) => {
    e.preventDefault();
    const targetId = e.currentTarget.getAttribute('href')?.substring(1);
    if (targetId) scrollToElement(targetId);
  }, []);

  const navLinks = [
    { href: '#about', label: t('nav.about') },
    { href: '#skills', label: t('nav.skills') },
    { href: '#projects', label: t('nav.projects') },
    { href: '#contact', label: t('nav.contact') },
  ];

  return (
    <footer className="relative border-t border-border-subtle text-text-primary overflow-hidden w-full">
      <div className="relative z-10 section-shell py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto w-full">
          <div className="card-glass p-6">
            <h3 className="text-lg font-bold text-text-primary mb-4">{t('footer.contact')}</h3>
            <div className="space-y-3 text-sm">
              <div>
                <span className="font-mono text-xs text-accent uppercase tracking-wider">Email</span>
                <a
                  href={`mailto:${APP_CONFIG.email}`}
                  className="block text-text-muted hover:text-accent transition-colors mt-1 break-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/50 rounded"
                  aria-label={`Send email to ${APP_CONFIG.email}`}
                >
                  {APP_CONFIG.email}
                </a>
              </div>
              <div>
                <span className="font-mono text-xs text-accent uppercase tracking-wider">Location</span>
                <p className="text-text-muted mt-1">Cosmópolis, SP</p>
              </div>
            </div>
          </div>

          <div className="card-glass p-6">
            <h3 className="text-lg font-bold text-text-primary mb-4">{t('footer.links')}</h3>
            <ul className="grid grid-cols-2 gap-2">
              {navLinks.map((link) => (
                <li key={link.href}>
                  <a
                    href={link.href}
                    onClick={handleNavClick}
                    className="text-sm text-text-muted hover:text-accent transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/50 rounded px-1"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div className="card-glass p-6">
            <h3 className="text-lg font-bold text-text-primary mb-4">{t('footer.socialMedia')}</h3>
            <div className="flex gap-3">
              <a
                href={APP_CONFIG.socialLinks.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
                className="p-3 rounded-lg border border-border-subtle text-text-muted hover:text-accent hover:border-accent/40 transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/50"
              >
                <LinkedinIcon className="w-5 h-5" style={{ fill: 'currentColor' }} aria-hidden="true" />
              </a>
              <a
                href={APP_CONFIG.socialLinks.github}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="GitHub"
                className="p-3 rounded-lg border border-border-subtle text-text-muted hover:text-accent hover:border-accent/40 transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/50"
              >
                <GithubIcon className="w-5 h-5" style={{ fill: 'currentColor' }} aria-hidden="true" />
              </a>
              <a
                href={`https://api.whatsapp.com/send?phone=${APP_CONFIG.socialLinks.whatsapp}`}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="WhatsApp"
                className="p-3 rounded-lg border border-border-subtle text-text-muted hover:text-accent hover:border-accent/40 transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/50"
              >
                <WhatsappIcon className="w-5 h-5" style={{ fill: 'currentColor' }} aria-hidden="true" />
              </a>
            </div>
          </div>
        </div>

        <div className="text-center mt-12 pt-8 border-t border-border-subtle">
          <p className="text-text-muted text-sm">
            © {new Date().getFullYear()} Andresa Alves. {t('footer.allRightsReserved')}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default memo(Footer);
